import { apiFetch } from "@/lib/api";
import { formatShipToBlock } from "@/lib/formatShipToBlock";
import { skuRequiresImeiIccid } from "@/pages/manufacturing/configurationDeviceFields";
import { fetchDeviceLookupReport } from "@/pages/manufacturing/deviceLookupApi";
import {
  kittingBatchPeekContext,
  type KittingBatchPeekApi,
} from "@/pages/manufacturing/kittingBatchPeek";
import { fetchShipmentDetail } from "@/pages/shipping/shipmentDetailsApi";

import type { BatchPeekContext, DevicePeekContext, ShipmentPeekContext } from "./entityPeekTypes";
import type { SalesOrderPeekContext } from "./SalesOrderPeekHoverHintBody";

export const ENTITY_PEEK_STALE_MS = 30_000;

const SUNTRONIC_LOCATION_ID = 2;
const KODIAK_LOCATION_ID = 14;

/** Barcode-only PCBA manufacturer site (matches quality-rma-helpers when build-order site is unknown). */
export function pcbaManufacturerLocationIdFromSerial(pcbaSerial: string | null | undefined): number | null {
  const serial = (pcbaSerial ?? "").trim();
  if (serial === "") return null;
  if (/^SD-\d{4}-\d{4}-\d+$/i.test(serial)) return SUNTRONIC_LOCATION_ID;
  if (/^[0-9]{5}-/.test(serial) || /^[4-9][0-9]{8,9}$/.test(serial)) return KODIAK_LOCATION_ID;
  return null;
}

export function devicePeekQueryKey(serial: string): readonly ["hub", "entity-peek", "device", string] {
  return ["hub", "entity-peek", "device", serial.trim().toUpperCase()];
}

/** True when the page only passed a serial and the hover body still needs hub manufacturing fields. */
export function devicePeekNeedsLookup(context: DevicePeekContext): boolean {
  return (
    (context.model == null || String(context.model).trim() === "") &&
    (context.firmwareVersion == null || String(context.firmwareVersion).trim() === "") &&
    (context.pcbaLocationId == null || context.pcbaLocationId <= 0) &&
    context.communicating !== true
  );
}

export async function fetchDevicePeekContext(serial: string): Promise<DevicePeekContext> {
  const trimmed = serial.trim();
  const report = await fetchDeviceLookupReport(trimmed);
  const finishedSku = report.serialFci.finishedSku?.trim() || null;
  const imei = report.serialFci.imei?.trim() || null;
  return {
    serial: report.serialFci.deviceLabelSerial?.trim() || trimmed,
    model: finishedSku,
    firmwareVersion: report.serialFci.firmwareVersion?.trim() || null,
    pcbaLocationId: pcbaManufacturerLocationIdFromSerial(report.serialFci.pcbaSerial),
    communicating: Boolean(imei) || (finishedSku != null && skuRequiresImeiIccid(finishedSku)),
  };
}

export function batchPeekQueryKey(batchId: number): readonly ["hub", "entity-peek", "batch", number] {
  return ["hub", "entity-peek", "batch", batchId];
}

export function shipmentPeekQueryKey(
  shipmentId: number,
): readonly ["hub", "entity-peek", "shipment", number] {
  return ["hub", "entity-peek", "shipment", shipmentId];
}

export function salesOrderPeekQueryKey(
  salesOrderId: number,
): readonly ["hub", "entity-peek", "sales_order", number] {
  return ["hub", "entity-peek", "sales_order", salesOrderId];
}

function lookupBatchPeekApi(
  peeks: Record<string, KittingBatchPeekApi>,
  batchId: number,
): KittingBatchPeekApi | undefined {
  return peeks[String(batchId)];
}

export async function fetchBatchPeekContext(batchId: number): Promise<BatchPeekContext | undefined> {
  const data = await apiFetch<{ peeks: Record<string, KittingBatchPeekApi> }>(
    "/api/manufacturing/batch-peeks",
    { query: { ids: String(batchId) } },
  );
  const apiPeek = lookupBatchPeekApi(data.peeks, batchId);
  return kittingBatchPeekContext({ apiPeek, loading: false });
}

export async function fetchShipmentPeekContext(shipmentId: number): Promise<ShipmentPeekContext | undefined> {
  const shipment = await fetchShipmentDetail(shipmentId);
  const shipToLabel = formatShipToBlock({
    ship_to_recipient: shipment.batch_ship_to_recipient,
    ship_to_address_line_1: shipment.batch_ship_to_address_line_1,
    ship_to_address_line_2: shipment.batch_ship_to_address_line_2,
    ship_to_address_line_3: shipment.batch_ship_to_address_line_3,
    ship_to_city: shipment.batch_ship_to_city,
    ship_to_state: shipment.batch_ship_to_state,
    ship_to_zip: shipment.batch_ship_to_zip,
    ship_to_country: shipment.batch_ship_to_country,
  });
  const context: ShipmentPeekContext = {
    ship_date: shipment.date_shipped,
    ship_by_date: shipment.batch_mfr_ship_by_date,
    ship_to_label: shipToLabel,
    batch_location_id: shipment.batch_location_id,
    batch_location_label: shipment.batch_location_label,
  };
  const hasLocation =
    (context.batch_location_id != null && context.batch_location_id > 0) ||
    (context.batch_location_label != null && context.batch_location_label.trim() !== "");
  if (context.ship_date == null && context.ship_by_date == null && context.ship_to_label == null && !hasLocation) {
    return undefined;
  }
  return context;
}

type SalesOrderPeekApi = {
  sales_order_id: number;
  sales_order_number: string | null;
  client_name: string | null;
  end_user_name: string | null;
  customer_purchase_order_number: string | null;
  sgs_ship_by_date: string | null;
  lines: { sku: string; quantity: number }[];
};

function lookupSalesOrderPeekApi(
  peeks: Record<string, SalesOrderPeekApi>,
  salesOrderId: number,
): SalesOrderPeekApi | undefined {
  return peeks[String(salesOrderId)];
}

function mapSalesOrderPeekApi(apiPeek: SalesOrderPeekApi): SalesOrderPeekContext {
  return {
    sales_order_number: apiPeek.sales_order_number,
    client_name: apiPeek.client_name,
    end_user_name: apiPeek.end_user_name,
    customer_purchase_order_number: apiPeek.customer_purchase_order_number,
    sgs_ship_by_date: apiPeek.sgs_ship_by_date,
    lines: apiPeek.lines,
  };
}

export async function fetchSalesOrderPeekContext(
  salesOrderId: number,
): Promise<SalesOrderPeekContext | undefined> {
  const data = await apiFetch<{ peeks: Record<string, SalesOrderPeekApi> }>(
    "/api/manufacturing/sales-order-peeks",
    { query: { ids: String(salesOrderId) } },
  );
  const apiPeek = lookupSalesOrderPeekApi(data.peeks, salesOrderId);
  return apiPeek != null ? mapSalesOrderPeekApi(apiPeek) : undefined;
}
